import React, {
  PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Footer } from '../Footer';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

interface IProps {
  /**
   * className for the `<main>` element
   */
  className?: string;

  rootClassName?: string;
  /**
   * props for the child container
   */
  containerProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;

  /**
   * props for the footer component container
   */
  footerProps?: React.ComponentProps<typeof Footer>;
  /**
   * to remove the default footer from DOM
   */
  hideFooter?: boolean;

  fullWidth?: boolean;
}

type TDimensions = {
  width: number | null;
  height: number | null;
};

interface IRenderPropValues {
  footerDimensions: TDimensions;
}

const Main = ({
  className,
  rootClassName,
  children,
  containerProps,
  footerProps,
  hideFooter,
  fullWidth,
}: IProps & {
  children:
    | PropsWithChildren['children']
    | ((values: IRenderPropValues) => JSX.Element);
}) => {
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerDimensions, setFooterDimensions] = useState<TDimensions>({
    width: null,
    height: null,
  });
  // const footerRef = useRef();

  useLayoutEffect(() => {
    if (footerRef.current) {
      const { width, height } = footerRef.current.getBoundingClientRect();
      setFooterDimensions({ width, height });
    }
  }, []);

  return (
    <div
      // added min-h-100vh for fallback
      className={twMerge(
        'flex flex-col min-h-[100vh] min-h-[100dvh] bg-white',
        rootClassName,
      )}
    >
      <main
        className={twMerge(
          clsx({
            'px-2 w-full mx-auto max-w-[1120px]': true,
          }),
          className,
        )}
      >
        <div
          data-testid="container-div" // Add this line
          className={twMerge(
            clsx({
              'w-full mx-auto': true,
              'max-w-[1024px] ml-auto mr-0': fullWidth,
              'max-w-[928px]': !fullWidth,
            }),
            className,
          )}
          {...(containerProps ?? {})}
        >
          {typeof children === 'function'
            ? children({
                footerDimensions,
              })
            : children}
        </div>
      </main>
      {hideFooter ? null : (
        <Footer role="contentInfo" {...(footerProps ?? {})} ref={footerRef} />
      )}
    </div>
  );
};

const Layout = {
  Main,
  Footer,
};

export default Layout;
